import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import axios from "axios";

const Loader = () => (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(255,255,255,0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 2000,
    }}
  >
    <div
      className="spinner-border text-primary"
      style={{
        width: "4rem",
        height: "4rem",
        color: "#06d1c6",
        borderWidth: "6px",
      }}
      role="status"
    >
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

export default function AdvisorNavbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("advisor_sidebar_collapsed") === "true",
  );
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 991 : false,
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [inlineOpen, setInlineOpen] = useState(null); // "clients" | "mutual-funds" | "research" | "support" | "earnings" | null
  const [floatingOpen, setFloatingOpen] = useState(null);

  const clientsRef = useRef(null);
  const mutualFundsRef = useRef(null);
  const researchRef = useRef(null);
  const supportRef = useRef(null);
  const earningsRef = useRef(null);
  const floatingRef = useRef(null);

  const menuItems = [
    { name: "Dashboard", path: "/agent/dashboard" },
  ];

  // ==============================
  // LOGOUT FUNCTION START
  // ==============================
  const handleLogout = async () => {
    Swal.fire({
      title: "Logout Confirmation",
      html: `
      <div style="text-align:center;">
        <div style="width:70px;height:70px;margin:0 auto 15px auto;border-radius:50%;background:#4bded63d;display:flex;align-items:center;justify-content:center;">
          <i class="fa fa-sign-out-alt" style="font-size:32px;color:#06d1c6;"></i>
        </div>
        <p style="font-size:16px;color:#555;margin:0;">
          Are you sure you want to logout from your account?
        </p>
      </div>
    `,
      showCancelButton: true,
      confirmButtonText: "Yes, Logout",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        popup: "custom-swal-popup",
        confirmButton: "custom-confirm-btn",
        cancelButton: "custom-cancel-btn",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);

        axios
          .post(`${process.env.REACT_APP_BACKEND_BASE_URL}/user/logout`)
          .then((res) => {
            if (res.status === 200) {
              localStorage.removeItem(process.env.REACT_APP_LOGIN_DATA_STORE);
              localStorage.removeItem("authToken");

              showLogoutSuccess();
              navigate("/");
            }
          })
          .catch(() => {
            Swal.fire("Error", "Something went wrong", "error");
          })
          .finally(() => setLoading(false));
      }
    });
  };

  const showLogoutSuccess = () => {
    Swal.fire({
      html: `
      <div style="text-align:center;padding:20px 10px;">
        <div style="width:80px;height:80px;margin:0 auto 20px auto;border-radius:50%;background:#4bded63d;display:flex;align-items:center;justify-content:center;">
          <i class="fa fa-check" style="font-size:36px;color:#06d1c6;"></i>
        </div>
        <h2 style="margin:0;font-size:22px;color:#333;">Logged out successfully</h2>
        <p style="margin-top:8px;color:#777;font-size:14px;">
          You have been safely logged out.
        </p>
      </div>
    `,
      showConfirmButton: false,
      timer: 1500,
      backdrop: `rgba(0,0,0,0.4)`,
      customClass: {
        popup: "custom-swal-popup",
      },
    });
  };
  // ==============================
  // LOGOUT FUNCTION END
  // ==============================

  useEffect(() => {
    const width = collapsed ? "56px" : "250px";
    document.documentElement.style.setProperty("--advisor-sidebar-width", width);
    if (collapsed) document.body.classList.add("advisor-sidebar-collapsed");
    else document.body.classList.remove("advisor-sidebar-collapsed");
  }, [collapsed]);

  useEffect(() => {
    function onResize() {
      const small = window.innerWidth <= 991;
      setIsMobile(small);
      if (small) setMobileOpen(false);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (collapsed) setInlineOpen(null);
    else setFloatingOpen(null);
  }, [collapsed]);

  useEffect(() => {
    function onDocClick(e) {
      if (
        floatingOpen &&
        floatingRef.current &&
        !floatingRef.current.contains(e.target)
      ) {
        const refMap = {
          clients: clientsRef,
          "mutual-funds": mutualFundsRef,
          research: researchRef,
          support: supportRef,
          earnings: earningsRef,
        };
        const trigger = refMap[floatingOpen]?.current;
        if (trigger && !trigger.contains(e.target)) setFloatingOpen(null);
      }
      if (mobileOpen) {
        const sidebarEl = document.querySelector(".advisor-sidebar");
        if (sidebarEl && !sidebarEl.contains(e.target)) setMobileOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === "Escape") {
        setFloatingOpen(null);
        setInlineOpen(null);
        setMobileOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [floatingOpen, mobileOpen]);

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("advisor_sidebar_collapsed", next ? "true" : "false");
  };

  const toggleDropdown = (menuName) => {
    if (isMobile) {
      setInlineOpen((v) => (v === menuName ? null : menuName));
      setFloatingOpen(null);
      return;
    }
    if (collapsed) {
      setFloatingOpen((v) => (v === menuName ? null : menuName));
      setInlineOpen(null);
    } else {
      setInlineOpen((v) => (v === menuName ? null : menuName));
      setFloatingOpen(null);
    }
  };

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "/css/style.css";
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    setTimeout(() => setLoading(false), 600);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const openMobileSidebar = () => setMobileOpen(true);

  const handleNavClick = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  if (loading) return <Loader />;

  return (
    <>
      <style>{`
      .custom-swal-popup {
  border-radius: 16px !important;
  padding: 25px !important;

}

.custom-confirm-btn {
  background: linear-gradient(90deg, #06b6a4, #06d1c6) !important;
  color: #fff !important;
  padding: 10px 22px !important;
  border-radius: 8px !important;
  border: none !important;
  margin-right: 10px;
  font-size: 14px;
  transition: 0.3s ease;
}

.custom-confirm-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 15px rgba(221, 36, 118, 0.3);
}

.custom-cancel-btn {
  background: #ef4444 !important;
  color: #fff !important;
  padding: 10px 22px !important;
  border-radius: 8px !important;
  border: none !important;
  font-size: 14px;
  transition: 0.3s ease;
}

.custom-cancel-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 15px rgba(221, 36, 118, 0.3);
}

.brand-logo {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
}

.brand-logo img {
  max-width: 140px;
  height: auto;
}

.page-content {
  margin-left: var(--advisor-sidebar-width, 250px);
  padding: 48px 25px 0 50px;  
  min-height: 100vh;
  background: #f9fefe;
  transition: margin-left 220ms ease;
}
body.advisor-sidebar-collapsed .page-content {
  margin-left: var(--advisor-sidebar-collapsed-width, 56px);
}

:root {
  --advisor-sidebar-width: 250px;
  --advisor-sidebar-collapsed-width: 56px;
}

.advisor-sidebar {
  position: fixed;
  top: 0;
  left: 0;
  min-width: var(--advisor-sidebar-width);
  width: max-content;
  height: 100vh;
  background: linear-gradient(180deg, #00bfa6 0%, #00a6d6 100%);
  color: #fff;
  display: flex;
  flex-direction: column;
  padding: 14px 10px;
  box-sizing: border-box;
  z-index: 1500;
  transition: min-width 220ms ease, padding 220ms ease;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;
  box-shadow: 0 8px 24px rgba(2,6,23,0.08);
}

.advisor-sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
  flex: 1;
  overflow-y: auto;
  padding-right: 6px;
  scrollbar-width: none;
}
.advisor-sidebar-nav::-webkit-scrollbar {
  display: none;
}

.advisor-sidebar.collapsed {
  min-width: var(--advisor-sidebar-collapsed-width);
  padding: 14px 8px;
}

.advisor-logo-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}
.advisor-logo-wrap img {
  width: 150px;
  border-radius: 10px;
  background: #fff;
  padding: 6px;
  transition: width 220ms ease, height 220ms ease;
}
.advisor-sidebar.collapsed .advisor-logo-wrap img {
  width: 40px;
  height: 40px;
}

.advisor-collapse-btn {
  margin-left: auto;
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 6px;
  font-size: 18px;
}

.advisor-menu-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 12px;
  border-radius: 10px;
  color: #eaf8f7;
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
  transition: background 140ms ease, transform 120ms ease;
  position: relative;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
  cursor: pointer;
}
.advisor-menu-item .icon {
  width: 36px;
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  background: rgba(255,255,255,0.06);
  border-radius: 8px;
  margin: 0;
}
.advisor-menu-item .label {
  line-height: 1;
  color: #eaf8f7;
}
.advisor-menu-item:hover {
  background: rgba(255,255,255,0.06);
  transform: translateX(4px);
  color: #fff;
}
.advisor-menu-item.active {
  background: rgba(255,255,255,0.12);
  box-shadow: 0 8px 18px rgba(0,0,0,0.08);
  color: #fff;
}

.advisor-sidebar.collapsed .advisor-menu-item {
  justify-content: center;
  padding: 8px 6px;
}
.advisor-sidebar.collapsed .advisor-menu-item .label {
  display: none;
}
.advisor-sidebar.collapsed .advisor-menu-item::after {
  content: attr(data-label);
  position: absolute;
  left: calc(var(--advisor-sidebar-collapsed-width) + 12px);
  top: 50%;
  transform: translateY(-50%) translateX(-6px);
  background: #ffffff;
  color: #0f172a;
  padding: 8px 12px;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(7,12,34,0.12);
  white-space: nowrap;
  font-weight: 700;
  opacity: 0;
  pointer-events: none;
  transition: opacity 160ms ease, transform 160ms ease;
  z-index: 9999;
}
.advisor-sidebar.collapsed .advisor-menu-item:hover::after {
  opacity: 1;
  transform: translateY(-50%) translateX(0);
}

.advisor-submenu {
  margin-left: 6px;
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.advisor-submenu .advisor-menu-item {
  padding-left: 6px;
}

.advisor-floating-submenu {
  position: fixed;
  left: calc(var(--advisor-sidebar-collapsed-width) + 8px);
  transform: translateY(-50%);
  min-width: 200px;
  max-width: 280px;
  max-height: 80vh;
  overflow-y: auto;
  background: linear-gradient(304deg, #00a0fc, #0cc5a2);
  color: #0f172a;
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(7,12,34,0.14);
  padding: 10px;
  z-index: 2600;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.advisor-floating-submenu .advisor-menu-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 8px;
  border-radius: 10px;

}
.advisor-floating-submenu .advisor-menu-item .icon {
  width: 44px;
  height: 44px;
  font-size: 18px;
}
.advisor-floating-submenu .advisor-menu-item .label {
  display: block;
  margin-top: 0;
  font-weight: 700;
  font-size: 15px;
  color: #fff;
}

.advisor-sidebar.overlay {
  transform: translateX(0);
  left: 0;
}

.advisor-mobile-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 15, 20, 0.45);
  z-index: 1400;
}

@media (max-width: 991px) {
  .advisor-sidebar {
    height: 100vh !important;
    max-height: 100vh !important;
    overflow-y: auto !important;
    padding-bottom: 50px !important;
    transform: translateX(-100%);
    transition: transform 220ms ease;
  }
  .advisor-sidebar.overlay {
    transform: translateX(0);
    left: 0;
  }
  .advisor-sidebar.collapsed {
    min-width: 250px;
    padding: 14px 10px;
  }
  .page-content {
  padding: 22% 5% 0 5%;
    margin-left: 0 !important;
  }
  .advisor-menu-item {
    padding: 10px;
    font-size: 14px;
  }
}

@media (max-width: 575px) {
  .advisor-sidebar {
    width: 250px;
    min-width: 250px;
  }
  .advisor-logo-wrap img {
    width: 120px;
  }
  .advisor-menu-item .icon {
    width: 40px;
    min-width: 40px;
    height: 40px;
  }
}

.nav {
  display: block;
  flex-wrap: wrap;
  padding-left: 0;
  margin-bottom: 0;
  list-style: none;
}
.sidebar-logout {
  margin-top: 15px;
}

@media only screen and (max-width: 767px) {
  .brand-logo {
    display: block;
  }
}

`}</style>

      {/* Mobile top bar */}
      {isMobile && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            top: 0,
            height: 70,
            background: "white",
            zIndex: 1401,
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "8px 12px",
          }}
        >
          <button
            type="button"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            style={{
              border: "none",
              background: "linear-gradient(90deg,#0bc1ab,#06a89d)",
              color: "#fff",
              borderRadius: 8,
              padding: "8px 10px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              boxShadow: "0 6px 18px rgba(11,183,165,0.08)",
              cursor: "pointer",
            }}
          >
            <i className={`fa ${mobileOpen ? "fa-times" : "fa-bars"}`} />
          </button>
          <Link to="/" style={{ textDecoration: "none", color: "#0d233e" }}>
            <img src="/images/gpslogo.png" alt="GPS logo" style={{ height: 40 }} />
          </Link>
        </div>
      )}

      {/* Sidebar (desktop: collapsible; mobile: overlay) */}
      <aside
        id="advisor-sidebar"
        className={`advisor-sidebar ${collapsed && !isMobile ? "collapsed" : ""} ${mobileOpen && isMobile ? "overlay" : ""}`}
        aria-label="Primary"
        style={{ top: isMobile ? 70 : 0 }}
      >
        <div className="advisor-logo-wrap" title="GPS">
          <Link
            to="/"
            onClick={() => {
              window.scrollTo({ top: 0 });
              if (mobileOpen) setMobileOpen(false);
            }}
            style={{ display: "flex", alignItems: "center", gap: 12 }}
          >
            <img src="/images/gpslogo.png" alt="GPS logo" />
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {!isMobile && (
              <button
                type="button"
                className="advisor-collapse-btn"
                onClick={toggleCollapse}
                title={collapsed ? "Expand menu" : "Collapse menu"}
                aria-label={collapsed ? "Expand menu" : "Collapse menu"}
              >
                <i className={`fa ${collapsed ? "fa-chevron-right" : "fa-bars"}`} />
              </button>
            )}
            {isMobile && (
              <button
                type="button"
                className="advisor-collapse-btn"
                onClick={() => setMobileOpen(false)}
                title="Close menu"
                aria-label="Close menu"
                style={{ color: "#fff" }}
              >
                <i className="fa fa-times" />
              </button>
            )}
          </div>
        </div>

        <nav className="advisor-sidebar-nav" role="navigation" aria-label="Main menu">
          {/* Dashboard */}
          <Link
            to={menuItems[0].path}
            className={`advisor-menu-item ${location.pathname === menuItems[0].path ? "active" : ""}`}
            data-label="Dashboard"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon"><i className="fa-solid fa-house" aria-hidden /></span>
            <span className="label">Dashboard</span>
          </Link>

          {/* Clients */}
          <div
            ref={clientsRef}
            className={`advisor-menu-item ${inlineOpen === "clients" && !collapsed ? "active" : ""}`}
            data-label="Clients"
            role="button"
            onClick={() => toggleDropdown("clients")}
            aria-haspopup="true"
            aria-expanded={collapsed ? floatingOpen === "clients" : inlineOpen === "clients"}
          >
            <span className="icon"><i className="fa-solid fa-users" aria-hidden /></span>
            <div style={{ display: collapsed && !isMobile ? "none" : "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <span className="label">Clients</span>
              {!isMobile && <i className={`fa fa-chevron-${inlineOpen === "clients" ? "up" : "down"}`} />}
            </div>
          </div>
          {inlineOpen === "clients" && (!collapsed || isMobile) && (
            <div className="advisor-submenu" role="menu">
              <Link to="/agent/clients/list" className="advisor-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-list" /></span>
                <span className="label">Client List</span>
              </Link>
              <Link to="/agent/clients/onboard" className="advisor-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-user-plus" /></span>
                <span className="label">Onboard Client</span>
              </Link>
              <Link to="/agent/clients/profile" className="advisor-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-user-circle" /></span>
                <span className="label">Client Profile</span>
              </Link>
            </div>
          )}

          {/* Mutual Funds */}
          <div
            ref={mutualFundsRef}
            className={`advisor-menu-item ${(inlineOpen === "mutual-funds" || location.pathname.includes("/agent/mutual-funds") || location.pathname.includes("/agent/failed-orders")) && !collapsed ? "active" : ""}`}
            data-label="Mutual Funds"
            role="button"
            onClick={() => toggleDropdown("mutual-funds")}
            aria-haspopup="true"
            aria-expanded={collapsed ? floatingOpen === "mutual-funds" : inlineOpen === "mutual-funds"}
          >
            <span className="icon"><i className="fa-solid fa-coins" aria-hidden /></span>
            <div style={{ display: collapsed && !isMobile ? "none" : "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <span className="label">Mutual Funds</span>
              {!isMobile && <i className={`fa fa-chevron-${inlineOpen === "mutual-funds" ? "up" : "down"}`} />}
            </div>
          </div>
          {inlineOpen === "mutual-funds" && (!collapsed || isMobile) && (
            <div className="advisor-submenu" role="menu">
              <Link to="/agent/mutual-funds/client-portfolio" className="advisor-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-briefcase" /></span>
                <span className="label">Client Portfolio (View)</span>
              </Link>
              <Link to="/agent/mutual-funds/draft-orders" className="advisor-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-file-alt" /></span>
                <span className="label">Draft Orders</span>
              </Link>
              <Link to="/agent/failed-orders" className="advisor-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-exclamation-triangle" /></span>
                <span className="label">Failed Orders</span>
              </Link>
            </div>
          )}

          {/* Research */}
          {/* <div
            ref={researchRef}
            className={`advisor-menu-item ${inlineOpen === "research" && !collapsed ? "active" : ""}`}
            data-label="Research"
            role="button"
            onClick={() => toggleDropdown("research")}
            aria-haspopup="true"
            aria-expanded={collapsed ? floatingOpen === "research" : inlineOpen === "research"}
          >
            <span className="icon"><i className="fa-solid fa-chart-line" aria-hidden /></span>
            <div style={{ display: collapsed && !isMobile ? "none" : "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <span className="label">Research</span>
              {!isMobile && <i className={`fa fa-chevron-${inlineOpen === "research" ? "up" : "down"}`} />}
            </div>
          </div>
          {inlineOpen === "research" && (!collapsed || isMobile) && (
            <div className="advisor-submenu" role="menu">
              <Link to="/agent/research/plan-sales" className="advisor-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-tags" /></span>
                <span className="label">Plan Sales</span>
              </Link>
            </div>
          )} */}

          {/* Support */}
          <div
            ref={supportRef}
            className={`advisor-menu-item ${inlineOpen === "support" && !collapsed ? "active" : ""}`}
            data-label="Support"
            role="button"
            onClick={() => toggleDropdown("support")}
            aria-haspopup="true"
            aria-expanded={collapsed ? floatingOpen === "support" : inlineOpen === "support"}
          >
            <span className="icon"><i className="fa-solid fa-headset" aria-hidden /></span>
            <div style={{ display: collapsed && !isMobile ? "none" : "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <span className="label">Support</span>
              {!isMobile && <i className={`fa fa-chevron-${inlineOpen === "support" ? "up" : "down"}`} />}
            </div>
          </div>
          {inlineOpen === "support" && (!collapsed || isMobile) && (
            <div className="advisor-submenu" role="menu">
              <Link to="/agent/support/client-tickets" className="advisor-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-ticket-alt" /></span>
                <span className="label">Client Tickets</span>
              </Link>
            </div>
          )}

          {/* Earnings */}
          <div
            ref={earningsRef}
            className={`advisor-menu-item ${inlineOpen === "earnings" && !collapsed ? "active" : ""}`}
            data-label="Earnings"
            role="button"
            onClick={() => toggleDropdown("earnings")}
            aria-haspopup="true"
            aria-expanded={collapsed ? floatingOpen === "earnings" : inlineOpen === "earnings"}
          >
            <span className="icon"><i className="fa-solid fa-rupee-sign" aria-hidden /></span>
            <div style={{ display: collapsed && !isMobile ? "none" : "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <span className="label">Earnings</span>
              {!isMobile && <i className={`fa fa-chevron-${inlineOpen === "earnings" ? "up" : "down"}`} />}
            </div>
          </div>
          {inlineOpen === "earnings" && (!collapsed || isMobile) && (
            <div className="advisor-submenu" role="menu">
              <Link to="/agent/earnings/commissions" className="advisor-menu-item" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-money-bill-wave" /></span>
                <span className="label">Commissions</span>
              </Link>
            </div>
          )}

          {/* Logout */}
          <button
            type="button"
            onClick={handleLogout}
            className="advisor-menu-item"
            data-label="Logout"
            title="Logout"
            style={{ background: "transparent", border: "none", textAlign: "left", width: "100%", marginTop: 8 }}
          >
            <span className="icon"><i className="fa-solid fa-arrow-right-from-bracket" /></span>
            <span className="label">Logout</span>
          </button>
        </nav>
      </aside>

      {/* Backdrop for mobile overlay */}
      {isMobile && mobileOpen && (
        <div
          className="advisor-mobile-backdrop"
          role="button"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Floating submenu when sidebar collapsed (desktop) */}
      {floatingOpen && !isMobile && (
        <div
          ref={floatingRef}
          className="advisor-floating-submenu"
          role="menu"
          aria-label={`${floatingOpen} submenu`}
          style={{ top: "35%" }}
        >
          {floatingOpen === "clients" && (
            <>
              <Link to="/agent/clients/list" className="advisor-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-list" /></span>
                <span className="label">Client List</span>
              </Link>
              <Link to="/agent/clients/onboard" className="advisor-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-user-plus" /></span>
                <span className="label">Onboard Client</span>
              </Link>
              <Link to="/agent/clients/profile" className="advisor-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-user-circle" /></span>
                <span className="label">Client Profile</span>
              </Link>
            </>
          )}
          {floatingOpen === "mutual-funds" && (
            <>
              <Link to="/agent/mutual-funds/client-portfolio" className="advisor-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-briefcase" /></span>
                <span className="label">Client Portfolio (View)</span>
              </Link>
              <Link to="/agent/mutual-funds/draft-orders" className="advisor-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-file-alt" /></span>
                <span className="label">Draft Orders</span>
              </Link>
              <Link to="/agent/failed-orders" className="advisor-menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-exclamation-triangle" /></span>
                <span className="label">Failed Orders</span>
              </Link>
            </>
          )}
          {floatingOpen === "research" && (
            <Link to="/agent/research/plan-sales" className="advisor-menu-item" onClick={() => setFloatingOpen(null)}>
              <span className="icon"><i className="fa-solid fa-tags" /></span>
              <span className="label">Plan Sales</span>
            </Link>
          )}
          {floatingOpen === "support" && (
            <Link to="/agent/support/client-tickets" className="advisor-menu-item" onClick={() => setFloatingOpen(null)}>
              <span className="icon"><i className="fa-solid fa-ticket-alt" /></span>
              <span className="label">Client Tickets</span>
            </Link>
          )}
          {floatingOpen === "earnings" && (
            <Link to="/agent/earnings/commissions" className="advisor-menu-item" onClick={() => setFloatingOpen(null)}>
              <span className="icon"><i className="fa-solid fa-money-bill-wave" /></span>
              <span className="label">Commissions</span>
            </Link>
          )}
        </div>
      )}
    </>
  );
}
